import { useEffect } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { RouteFallback } from '@/shared/components/RouteFallback';
import { useOrg } from '@/shared/org';
import { NotFound } from './NotFound';

/**
 * numtable.com/<business> — the address an employer hands its staff.
 *
 * It is an ENTRY POINT, not a URL prefix. The slug is resolved, that
 * organization becomes the active one, and the user is sent to the ordinary
 * `/` — from there every link in the app is the same link it has always been.
 *
 * The alternative, keeping `/acme/` in front of every route, was rejected: it
 * would mean rewriting every `NavLink` and `navigate()` call in the suite, and
 * any one that was missed would silently drop the employee back into whichever
 * organization they were last in. A prefix that is correct in most places is
 * worse than no prefix, because the failure is invisible. This way there is
 * exactly one canonical URL shape and the vanity address is a door into it.
 *
 * Resolution is against the user's OWN memberships, never a lookup by slug.
 * That is deliberate on two counts: it costs no query, and a stranger probing
 * `/somecompany` learns nothing — the page is a 404 for them whether or not the
 * organization exists.
 */
export function OrgSlugEntry() {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const org = useOrg();

  const match =
    org.status === 'ready' && orgSlug
      ? (org.memberships.find((m) => m.organization.publicSlug === orgSlug) ?? null)
      : null;

  useEffect(() => {
    // Switching inside an effect, not during render: `setActiveOrg` writes to
    // the provider and persists `profiles.last_org_id`.
    if (match && org.status === 'ready' && match.orgId !== org.orgId) {
      org.setActiveOrg(match.orgId);
    }
  }, [match?.orgId, org.status]);

  if (org.status === 'loading') return <RouteFallback />;

  // Not a member of anything with that address — including the case where no
  // such organization exists. The two are the same answer on purpose.
  if (!match) return <NotFound />;

  return <Navigate to="/" replace />;
}
